import type { QueryClient } from "@tanstack/react-query";
import { createRoute, useLoaderData } from "@tanstack/react-router";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import type { ProductComparisonPageInitialData } from "~/app/api/pageData/productComparisonPageInitialData";
import { productComparisonPageQueryOptions } from "~/app/api/pageData/productComparisonPageQuery";
import { rootRoute } from "~/app/router/rootRoute";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { createProductComparisonPage } from "~/pages/ProductComparisonPage/ProductComparisonPage";

export interface ProductComparisonSearch {
  productCode: string | null;
}

export function parseProductComparisonSearch(search: Record<string, unknown>): ProductComparisonSearch {
  const productCode = typeof search.productCode === "string" ? search.productCode.trim() : "";
  return { productCode: productCode || null };
}

function ProductComparisonRoute() {
  const result = useLoaderData({
    from: "/product-comparison",
  }) as PageInitialData<ProductComparisonPageInitialData>;
  const shellProps = useStorefrontShellProps();

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {createProductComparisonPage(result.data?.productList ?? [])}
    </StorefrontShellFrame>
  );
}

export async function loadProductComparisonPageData(
  context: { queryClient: QueryClient },
  search: ProductComparisonSearch,
) {
  const result = await context.queryClient.ensureQueryData(productComparisonPageQueryOptions(search.productCode));

  return unwrapPageInitialData(result);
}

export const productComparisonRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/product-comparison",
  validateSearch: parseProductComparisonSearch,
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps }) => loadProductComparisonPageData(context, deps),
  component: ProductComparisonRoute,
});
